import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faBookOpen, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import './StudentArea.css';

const IMAGE_BASE = 'https://robomind-edu.com/assets/students/';

// "Karim Haddad" -> "Karim H." (protects students' privacy on a public page)
const displayName = (first = '', last = '') => {
  const f = String(first).trim();
  const l = String(last).trim();
  return l ? `${f} ${l.charAt(0).toUpperCase()}.` : f;
};

const initials = (first = '', last = '') =>
  `${String(first).trim().charAt(0)}${String(last).trim().charAt(0)}`.toUpperCase() || '?';

const StudentCard = ({ student }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const hasImage = student.Image && !imgFailed;
  const sessions = Number(student.StudentTotal) || 0;

  return (
    <article className="rmst-card">
      <div className="rmst-avatar">
        {hasImage ? (
          <img
            src={IMAGE_BASE + student.Image}
            alt={displayName(student.FirstName, student.LastName)}
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <span aria-hidden="true">{initials(student.FirstName, student.LastName)}</span>
        )}
      </div>

      <h3 className="rmst-name">{displayName(student.FirstName, student.LastName)}</h3>

      <div className="rmst-module">
        <FontAwesomeIcon icon={faBookOpen} className="rmst-module-icon" />
        <span>
          <small>Currently learning</small>
          {student.LastModuleName || 'Getting started'}
        </span>
      </div>

      {sessions > 0 && (
        <div className="rmst-sessions">
          <FontAwesomeIcon icon={faCircleCheck} />
          {sessions} {sessions === 1 ? 'class' : 'classes'} completed
        </div>
      )}
    </article>
  );
};

const StudentArea = () => {
  const [students, setStudents] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | hidden
  const rowRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStudents = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/students`);
        if (!response.ok) throw new Error(String(response.status));
        const data = await response.json();
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setStudents(data);
          setStatus('ready');
        } else {
          setStatus('hidden');
        }
      } catch (err) {
        if (!cancelled) setStatus('hidden'); // never show errors to visitors; just skip the section
      }
    };
    fetchStudents();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'hidden') return null;

  const scrollRow = (dir) => {
    const row = rowRef.current;
    if (!row) return;
    row.scrollBy({ left: dir * row.clientWidth * 0.85, behavior: 'smooth' });
  };

  return (
    <section className="rmst" aria-labelledby="rmst-title">
      <div className="rmst-inner">
        <header className="rmst-header">
          <div className="rmst-heading">
            <span className="rmst-eyebrow">Our students</span>
            <h2 id="rmst-title" className="rmst-title">
              Meet our <span>young creators</span>
            </h2>
          </div>
          {status === 'ready' && students.length > 3 && (
            <div className="rmst-arrows">
              <button type="button" className="rmst-arrow" aria-label="Previous students" onClick={() => scrollRow(-1)}>
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <button type="button" className="rmst-arrow" aria-label="Next students" onClick={() => scrollRow(1)}>
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          )}
        </header>

        <div className="rmst-row" ref={rowRef}>
          {status === 'loading'
            ? [0, 1, 2, 3].map((i) => <div key={i} className="rmst-card rmst-skeleton" aria-hidden="true" />)
            : students.map((student, index) => (
                <StudentCard key={student.Id ?? student.id ?? index} student={student} />
              ))}
        </div>
      </div>
    </section>
  );
};

export default StudentArea;